import { useEffect, useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import type {
  SaleWebhookInboxStatus,
  WebhookEventType,
} from '../../domain/entities/sales.entity';
import { useProviderAccounts } from '../hooks/use-provider-accounts';
import {
  EMPTY_SALE_WEBHOOK_INBOX_FILTERS,
  type SaleWebhookInboxFilters,
} from '../shared/sale-webhook-inbox-filters';
import {
  SALE_WEBHOOK_INBOX_STATUS_OPTIONS,
  webhookEventTypeLabel,
} from '../shared/display';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  ...SALE_WEBHOOK_INBOX_STATUS_OPTIONS,
];

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  ...(['transaction', 'subscription'] as const).map((eventType) => ({
    value: eventType,
    label: webhookEventTypeLabel(eventType),
  })),
];

interface SaleWebhookInboxFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: SaleWebhookInboxFilters;
  onApply: (filters: SaleWebhookInboxFilters) => void;
}

export function SaleWebhookInboxFiltersModal({
  open,
  onClose,
  filters,
  onApply,
}: SaleWebhookInboxFiltersModalProps) {
  const [draft, setDraft] = useState(filters);
  const { data: accounts } = useProviderAccounts();

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const update = <K extends keyof SaleWebhookInboxFilters>(
    key: K,
    value: SaleWebhookInboxFilters[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const accountOptions = [
    { value: '', label: 'Todas as contas' },
    ...(accounts ?? []).map((account) => ({
      value: account.id,
      label: account.displayName,
    })),
  ];

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filtrar webhook inbox"
      description="Filtra o log de webhooks recebidos direto no servidor."
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setDraft(EMPTY_SALE_WEBHOOK_INBOX_FILTERS)}
          >
            Limpar
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleApply}>
            Aplicar filtros
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Status"
          id="webhook-inbox-filter-status"
          options={STATUS_OPTIONS}
          value={draft.status}
          onChange={(e) =>
            update('status', e.target.value as SaleWebhookInboxStatus | '')
          }
        />
        <Select
          label="Tipo de evento"
          id="webhook-inbox-filter-event-type"
          options={EVENT_TYPE_OPTIONS}
          value={draft.eventType}
          onChange={(e) =>
            update('eventType', e.target.value as WebhookEventType | '')
          }
        />
        <Select
          label="Conta"
          id="webhook-inbox-filter-account"
          options={accountOptions}
          value={draft.providerAccountId}
          onChange={(e) => update('providerAccountId', e.target.value)}
        />
        <Input
          label="Origem"
          id="webhook-inbox-filter-idempotence-source"
          placeholder="ID da transação/assinatura no provedor"
          value={draft.idempotenceSource}
          onChange={(e) => update('idempotenceSource', e.target.value)}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="De"
            type="date"
            value={draft.from}
            onChange={(e) => update('from', e.target.value)}
          />
          <Input
            label="Até"
            type="date"
            value={draft.to}
            onChange={(e) => update('to', e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
